import React, { useState } from 'react';
import {
  Download,
  FileSpreadsheet,
  FileText,
  Printer,
  ChevronDown,
  Layers,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ActionTooltip } from '@/components/common/ActionTooltip';
import {
  CyclePerformanceData,
  exportPerformanceReportToCSV,
  exportPerformanceReportToExcel,
} from '@/services/reportService';

interface ExportDropdownProps {
  data: CyclePerformanceData[];
  filenamePrefix?: string;
  onPrintCurrentView?: () => void;
  onPrintManagementPack?: () => void;
  activeViewTitle?: string;
}

export const ExportDropdown: React.FC<ExportDropdownProps> = ({
  data,
  filenamePrefix = 'VSB_Production_Performance',
  onPrintCurrentView,
  onPrintManagementPack,
  activeViewTitle = 'Current View',
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleExportCSV = () => {
    exportPerformanceReportToCSV(data, `${filenamePrefix}_${new Date().toISOString().slice(0, 10)}.csv`);
    setIsOpen(false);
  };

  const handleExportExcel = () => {
    exportPerformanceReportToExcel(data, `${filenamePrefix}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    setIsOpen(false);
  };

  const handlePrintCurrent = () => {
    setIsOpen(false);
    onPrintCurrentView?.();
  };

  const handlePrintPack = () => {
    setIsOpen(false);
    onPrintManagementPack?.();
  };

  return (
    <div className="relative inline-block text-left">
      <ActionTooltip content="Export report tables to PDF, Excel (.xlsx), or CSV spreadsheet format.">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsOpen(!isOpen)}
          className="h-9 px-3 border-slate-300 text-slate-700 bg-white hover:bg-slate-50 flex items-center gap-1.5 text-xs font-semibold shadow-sm"
        >
          <Download className="w-4 h-4 text-slate-500" />
          <span>Export</span>
          <ChevronDown className="w-3.5 h-3.5 text-slate-400 ml-0.5" />
        </Button>
      </ActionTooltip>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 mt-1.5 w-60 rounded-xl bg-white shadow-xl border border-slate-200 z-30 py-1.5 text-xs divide-y divide-slate-100">
            {/* PDF / Print Options */}
            <div className="py-1">
              <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                PDF / Print Reports
              </div>
              <button
                onClick={handlePrintCurrent}
                className="w-full text-left px-3 py-2 flex items-center gap-2.5 hover:bg-slate-50 text-slate-800 font-medium transition-colors"
              >
                <Printer className="w-4 h-4 text-emerald-700 shrink-0" />
                <div>
                  <div className="font-semibold text-slate-900">Print / Save as PDF</div>
                  <div className="text-[10px] text-slate-500">{activeViewTitle}</div>
                </div>
              </button>

              <button
                onClick={handlePrintPack}
                className="w-full text-left px-3 py-2 flex items-center gap-2.5 hover:bg-slate-50 text-slate-800 font-medium transition-colors"
              >
                <Layers className="w-4 h-4 text-indigo-700 shrink-0" />
                <div>
                  <div className="font-semibold text-slate-900">Management Report Pack</div>
                  <div className="text-[10px] text-slate-500">Multi-page executive pack</div>
                </div>
              </button>
            </div>

            {/* Spreadsheet Data Exports */}
            <div className="py-1">
              <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Spreadsheet Exports
              </div>
              <button
                onClick={handleExportExcel}
                className="w-full text-left px-3 py-2 flex items-center gap-2.5 hover:bg-slate-50 text-slate-800 font-medium transition-colors"
              >
                <FileSpreadsheet className="w-4 h-4 text-emerald-600 shrink-0" />
                <div>
                  <div className="font-semibold text-slate-900">Export to Excel (.xlsx)</div>
                  <div className="text-[10px] text-slate-500">Performance cohort dataset</div>
                </div>
              </button>
              <button
                onClick={handleExportCSV}
                className="w-full text-left px-3 py-2 flex items-center gap-2.5 hover:bg-slate-50 text-slate-800 font-medium transition-colors"
              >
                <FileText className="w-4 h-4 text-blue-600 shrink-0" />
                <div>
                  <div className="font-semibold text-slate-900">Export to CSV (.csv)</div>
                  <div className="text-[10px] text-slate-500">Raw tabular comma-separated</div>
                </div>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
